import { VALID_URL_REGEX } from "@constants/global";
import type { LucideIcon } from "lucide-react";
import { ArrowDown, ArrowUp, Equal } from "lucide-react";
import escapeHtml from "escape-html";
import React, { ReactNode } from "react";
import { EOfferState } from "types/Jobyfine.type";
import { ETaskType } from "types/Task.type";
import { EUserSeniority } from "types/User.types";

const {
  uniqueNamesGenerator,
  adjectives,
  colors,
  animals
} = require("unique-names-generator");

export const numberWithDots = (str: string): string =>
  str.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");

export const capitalizeFirstLetter = (string: string): string => {
  if (!string) return string;

  return `${string?.charAt(0).toUpperCase()}${string?.toLowerCase().slice(1)}`;
};

export const removeAccents = (str: string): string =>
  str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

export const compareColor = (percent: number) => {
  const percentType =
    percent < 0 ? "#F87171" : percent === 0 ? "#FFAC3D" : "#34D399";

  return percentType;
};

export const compareIcon = (percent: number): LucideIcon => {
  if (percent < 0) return ArrowDown;
  if (percent === 0) return Equal;
  return ArrowUp;
};

export const percentColor = (percent: number) => {
  const percentType =
    percent < 30
      ? "#F87171"
      : percent >= 30 && percent < 90
        ? "#FFAC3D"
        : "#34D399";

  return percentType;
};

export const percentIcon = (percent: number): LucideIcon => {
  if (percent < 30) return ArrowDown;
  if (percent < 90) return Equal;
  return ArrowUp;
};

export const isUUID = (uuid: string): boolean => {
  const s = `${uuid}`;
  const test = s.match(
    "^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
  );

  if (test === null) {
    return false;
  }
  return true;
};

export const firstChars = (str: string): string => {
  if (!str) return "";
  const words = str.split(" ");
  let chars = "";

  words.forEach((w) => {
    chars += w[0];
  });

  return chars;
};

export type Color =
  | "red"
  | "orange"
  | "yellow"
  | "green"
  | "cyan"
  | "blue"
  | "violet";

// * random color
export const randomColor = (word: string) => {
  const posibleColors = [
    "red",
    "orange",
    "yellow",
    "green",
    "cyan",
    "blue",
    "violet"
  ];

  // * get random color based on word length
  return posibleColors[word.length % posibleColors.length] as Color;
};

export const colorByBackground = (bgColor: string) => {
  let cleanBgColor = bgColor;
  // If a leading # is provided, remove it
  if (cleanBgColor.slice(0, 1) === "#") {
    cleanBgColor = cleanBgColor.slice(1);
  }

  // Convert to RGB value
  const r = parseInt(cleanBgColor.substr(0, 2), 16);
  const g = parseInt(cleanBgColor.substr(2, 2), 16);
  const b = parseInt(cleanBgColor.substr(4, 2), 16);

  // Get YIQ ratio
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;

  // Check contrast
  return yiq >= 128 ? "#000000" : "#ffffff";
};

export const JSONParse = function <TParsedObj>(jsonstr: string): TParsedObj {
  return JSON.parse(jsonstr) as TParsedObj;
};

export const taskTypeIcon = (type: ETaskType): string => {
  let icon = "";
  if (type === ETaskType.BOOLEAN) {
    icon = "/svg/task-boolean.svg";
  } else if (type === ETaskType.NUMERIC) {
    icon = "/svg/task-numeric.svg";
  } else if (type === ETaskType.CURRENCY) {
    icon = "/svg/task-currency.svg";
  }
  return icon;
};

export const isName = (name: string): boolean => {
  // * validate only letters with diacritics and spaces
  const regex = /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]+$/;
  return regex.test(name);
};

export const checkIsEmptyParagraph = (paragraph: string) => {
  const data = paragraph
    .replace(/<[^>]*>/gi, " ")
    .replace(/<\/[^>]*>/gi, " ")
    .replace(/&nbsp;|&#160;/gi, " ")
    .replace(/\s+/gi, " ")
    .trim();

  return data.length === 0;
};

/**
 * Convierte HTML proveniente de editores rich-text (Quill/Slate) a texto plano,
 * apto para previews con `line-clamp`. Quita tags, decodifica entidades comunes
 * y colapsa espacios. Seguro para SSR (no usa DOM).
 */
export const htmlToPlainText = (html?: string | null): string => {
  if (!html) return "";

  return html
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<\/(p|div|h[1-6]|li|tr)>/gi, " ")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;|&#160;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, "\"")
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/\s+/g, " ")
    .trim();
};

export const isUrl = (str: string): boolean => {
  if (!str || str.length === 0) return false;
  const urlRegex = VALID_URL_REGEX;
  const url = new RegExp(urlRegex, "i");
  return str.length < 2083 && url.test(str);
};

export const getQsParams = <TURLParams extends {}>(): TURLParams => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  return Object.fromEntries(urlSearchParams.entries()) as TURLParams;
};

export const getRandomUsername = (): string =>
  uniqueNamesGenerator({
    dictionaries: [adjectives, colors, animals],
    length: 2,
    separator: "-",
    style: "lowerCase"
  });

export const userSeniorityDescription = (
  seniority: EUserSeniority,
  lang: string
): string => {
  let description = "";

  if (seniority === EUserSeniority.JUNIOR) {
    description = "Junior";
  } else if (seniority === EUserSeniority.SENIOR) {
    description = "Senior";
  } else if (seniority === EUserSeniority.SEMISENIOR) {
    description = "Semi Senior";
  } else if (seniority === EUserSeniority.UNDEFINED) {
    description = lang === "es" ? "Sin asignar" : "Unassigned";
  }

  return description;
};

export const makeBold = (text: string, boldText: string[]): string => {
  let bold = text;
  boldText.forEach((b) => {
    bold = bold.replace(b, `<b>${b}</b>`);
  });
  return bold;
};

const OFFER_STATUS = [
  {
    id: EOfferState.PUBLIC,
    name: "Pública",
    color: "cyan"
  },
  {
    id: EOfferState.CLOSED,
    name: "Cerrada",
    color: "green"
  },
  {
    id: EOfferState.CANCELED,
    name: "Cancelada",
    color: "red"
  },
  {
    id: EOfferState.STANDBY,
    name: "Stand by",
    color: "yellow"
  },
  {
    id: EOfferState.DRAFT,
    name: "Borrador",
    color: "violet"
  },
  {
    id: EOfferState.DEFAULT,
    name: "Sin oferta",
    color: "gray"
  }
];

export const colorState = (word: string) => {
  const state = OFFER_STATUS.find((s) => s.id === word);
  if (state === null || state === undefined) return "violet";
  return state.color as Color;
};

export const orderStringAsc = (a, b) => {
  if (a.label < b.label) return -1;
  if (a.label > b.label) return 1;
  return 0;
};

export const sizeInKb = (size: number): string => {
  const kb = size / 1024;
  return `${kb.toFixed(2)}KB`;
};

/**
 * Matches plain-text URLs (http/https) so they can be turned into clickable
 * anchors. Excludes characters that would never legitimately appear inside a
 * URL (whitespace, angle brackets, quotes) so attribute values in raw HTML are
 * never accidentally captured by linkification helpers below.
 */
const PLAIN_URL_REGEX = /(https?:\/\/[^\s<>"']+)/g;

/** Trim trailing punctuation that is usually NOT part of the URL itself. */
const stripTrailingPunctuation = (url: string): { url: string; trail: string } => {
  const m = url.match(/^(.*?)([.,;:!?\)\]\}]+)$/);
  if (!m) return { url, trail: "" };
  return { url: m[1], trail: m[2] };
};

const buildAnchorHtml = (href: string): string =>
  `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer" style="color:#0a66c2;text-decoration:underline;word-break:break-all">${escapeHtml(href)}</a>`;

/**
 * Replace plain-text URLs in an HTML string with anchor tags, without touching
 * the contents of existing <a> / <iframe> / <script> / <style> blocks nor any
 * tag attribute values. The function walks the string by tag boundaries so it
 * only ever rewrites text nodes.
 */
export const linkifyHtmlPreservingAnchors = (html: string): string => {
  if (!html) return html;
  // Fast path: nothing that looks like a URL.
  if (!/https?:\/\//i.test(html)) return html;

  const SKIP_TAGS = ["a", "iframe", "script", "style", "code", "pre"];
  const lower = html.toLowerCase();
  let out = "";
  let i = 0;

  const linkifyTextChunk = (chunk: string): string =>
    chunk.replace(PLAIN_URL_REGEX, (match) => {
      const { url, trail } = stripTrailingPunctuation(match);
      if (!url) return match;
      return buildAnchorHtml(url) + trail;
    });

  while (i < html.length) {
    const lt = html.indexOf("<", i);
    if (lt === -1) {
      out += linkifyTextChunk(html.slice(i));
      break;
    }
    // Text node before next tag.
    out += linkifyTextChunk(html.slice(i, lt));

    // Identify tag name.
    const tagMatch = /^<\s*\/?\s*([a-zA-Z][a-zA-Z0-9]*)/.exec(html.slice(lt));
    if (!tagMatch) {
      // Not a real tag; copy the `<` and continue.
      out += html[lt];
      i = lt + 1;
      continue;
    }
    const tagName = tagMatch[1].toLowerCase();

    if (SKIP_TAGS.includes(tagName) && !html.slice(lt, lt + 2).toLowerCase().startsWith("</")) {
      // Skip the entire block <tag ...> ... </tag>, copying it verbatim.
      const closeNeedle = `</${tagName}`;
      const closeIdx = lower.indexOf(closeNeedle, lt);
      if (closeIdx === -1) {
        out += html.slice(lt);
        break;
      }
      const closeEnd = html.indexOf(">", closeIdx);
      if (closeEnd === -1) {
        out += html.slice(lt);
        break;
      }
      out += html.slice(lt, closeEnd + 1);
      i = closeEnd + 1;
      continue;
    }

    // Regular tag: copy as-is up to its `>`.
    const gt = html.indexOf(">", lt);
    if (gt === -1) {
      out += html.slice(lt);
      break;
    }
    out += html.slice(lt, gt + 1);
    i = gt + 1;
  }
  return out;
};

/**
 * Render a plain string as React children, converting URL substrings into
 * clickable <a> elements. Useful for components that receive raw text (not
 * HTML), e.g. the `quote` slot of the EventCard.
 */
export const linkifyText = (
  text: string | null | undefined,
  anchorClassName?: string
): ReactNode => {
  if (!text) return text ?? null;
  if (!/https?:\/\//i.test(text)) return text;

  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  text.replace(PLAIN_URL_REGEX, (match, _g, offset: number) => {
    if (offset > lastIndex) {
      parts.push(text.slice(lastIndex, offset));
    }
    const { url, trail } = stripTrailingPunctuation(match);
    parts.push(
      React.createElement(
        "a",
        {
          key: `lnk-${key++}`,
          href: url,
          target: "_blank",
          rel: "noopener noreferrer",
          className: anchorClassName,
          style: anchorClassName
            ? undefined
            : { color: "#0a66c2", textDecoration: "underline", wordBreak: "break-all" },
          onClick: (e: React.MouseEvent) => e.stopPropagation(),
        },
        url
      )
    );
    if (trail) parts.push(trail);
    lastIndex = offset + match.length;
    return match;
  });
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
};

export const serializeNode = (
  node: any,
  theme: string,
  children: any,
  vendor: string,
  addMargin: boolean = false
) => {
  switch (node.type) {
    case "quote":
      return `<blockquote><p>${children}</p></blockquote>`;
    case "paragraph":
      return `<p style="${theme === "dark" ? "color: #fff" : ""}">${children}</p>${addMargin && children ? "<br />" : ""}`;
    case "link":
      return `<a style="color: blue" target="_blank" href="${escapeHtml(
        node.url
      )}">${children}</a>`;
    case "mention":
      return `<a style="color:#0a66c2; font-weight:600" href="${
        node?.character?.includes("[TEAM]")
          ? "#"
          : `/profile/${node.id}?vendorId=${vendor}`
      }">${node.character}</a> `;
    case "image": {
      const rawUrl = typeof node.url === "string" ? node.url.trim() : "";
      const safeUrl =
        /^(https?:\/\/|\/|data:image\/)/i.test(rawUrl) &&
        !/^javascript:/i.test(rawUrl)
          ? escapeHtml(rawUrl)
          : "";
      if (!safeUrl) return children ?? "";
      const safeAlt = escapeHtml(
        typeof node.alt === "string" ? node.alt : ""
      );
      return `<div style="display: flex; justify-content: center; padding-top:2px"><img width="300" height="300" src="${safeUrl}" alt="${safeAlt}" /></div>`;
    }
    case "file":
    case "attachment": {
      const rawUrl = typeof node.url === "string" ? node.url.trim() : "";
      const safeUrl =
        /^(https?:\/\/|\/)/i.test(rawUrl) && !/^javascript:/i.test(rawUrl)
          ? escapeHtml(rawUrl)
          : "";
      if (!safeUrl) return children ?? "";
      const label =
        typeof node.name === "string" && node.name.trim()
          ? node.name.trim()
          : "Archivo";
      return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer">📎 ${escapeHtml(label)}</a>`;
    }
    case "video":
    case "canva":
      // ponytail: fixed 16/9 box via native CSS aspect-ratio. The old
      // `responsive-iframe` class carried a `@media(max-width:768px){padding-bottom:75%}`
      // override that made the box far taller than these landscape Canva magazines,
      // leaving big empty bands top/bottom. 16/9 fits the designs; tune the ratio here.
      // Kept on a single line: this HTML renders inside a `whitespace-pre-wrap`
      // container, so any surrounding newlines/indentation show up as dead vertical
      // space above and below the embed.
      return `<div style="position:relative;width:100%;aspect-ratio:16/9;margin-top:2px;border-radius:7px;overflow:hidden;"><iframe id="${escapeHtml(node.url)}" key="${escapeHtml(node.url)}" src="${escapeHtml(node.url)}" alt="${escapeHtml(node.url)}" allow="fullscreen; autoplay; encrypted-media; screen-wake-lock; accelerometer; clipboard-write; gyroscope; picture-in-picture" title="${escapeHtml(node.url)}" style="position:absolute;inset:0;width:100%;height:100%;border:none;"></iframe></div>`;
    default:
      return children;
  }
};

export const serializeNodeForAnouncement = (node: any, children: any) => {
  switch (node.type) {
    case "mention":
      return node.id;
    default:
      return children;
  }
};

export const hasCommentContent = (value?: string | null): boolean => {
  if (!value?.trim()) return false;
  try {
    return checkIfNodeHaveText(JSON.parse(value));
  } catch {
    return false;
  }
};

export const extractSlateImageUrls = (value?: string | null): string[] => {
  if (!value?.trim()) return [];

  try {
    const nodes = JSON.parse(value);
    if (!Array.isArray(nodes)) return [];

    const urls: string[] = [];

    const walk = (node: unknown) => {
      if (!node || typeof node !== "object") return;

      const slateNode = node as {
        type?: string;
        url?: string;
        children?: unknown[];
      };

      if (slateNode.type === "image" && slateNode.url) {
        urls.push(slateNode.url);
      }

      slateNode.children?.forEach(walk);
    };

    nodes.forEach(walk);
    return [...new Set(urls)];
  } catch {
    return [];
  }
};

export const checkIfNodeHaveText = (blocks) => {
  for (const block of blocks) {
    if ("url" in block && block["url"] !== "") {
      return true;
    }

    if (block["children"]) {
      for (const child of block["children"]) {
        if (child["text"]) {
          return child["text"] !== "";
        }

        if (child["character"]) {
          return child["character"] !== "";
        }

        if (child["url"] || child["video"]) {
          return child["url"] !== "";
        }
      }
    }
  }

  return false;
};

export const firsLetterCapitalize = (str: string) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Formats a full name to show only the first name and first last name,
 * with proper capitalization (first letter uppercase, rest lowercase)
 * @example formatUserDisplayName("CESAR ANTONIO SALAS CARVAJAL") => "Cesar Salas"
 */
export const formatUserDisplayName = (
  firstName?: string,
  lastName?: string
): string => {
  if (!firstName && !lastName) return "";

  const formatName = (name: string): string => {
    if (!name) return "";
    return (
      name
        .trim()
        .split(" ")[0] // Take only the first word
        .toLowerCase()
        .charAt(0)
        .toUpperCase() + name.trim().split(" ")[0].toLowerCase().slice(1)
    );
  };

  const formattedFirstName = formatName(firstName || "");
  const formattedLastName = formatName(lastName || "");

  return [formattedFirstName, formattedLastName].filter(Boolean).join(" ");
};
